/* Mirrors andavo-dev's Nomad Tabs (libs/client/web/shared/ui/nomad/src/lib/Tabs).
   Pill segmented control: a `surface-foreground` rounded-full track with an
   animated white active pill bordered `border-medium`; the active label goes
   `font-semibold`. Reproduced 1:1 against the vendored tokens (TabsContentActions
   / useTabsActions context machinery omitted — not needed for the shell). */
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../util/cn';

const Tabs = TabsPrimitive.Root;

// `max-w-full` caps the inline-flex track to its container so it can't push the
// page wider on a phone; `overflow-x-auto` + `flex-nowrap` then lets the pill row
// SCROLL horizontally instead of overflowing/clipping when more tabs exist than
// fit (CRM has 9, Expense has 5 — they overflow 390px). `no-scrollbar` (the
// existing vendored utility in styles.css) keeps the scroll affordance touch-
// only, no chrome bar. The animated active pill is positioned via offsetLeft
// within this same scrolling content box, so it tracks the active tab correctly
// while scrolled.
const tabsListVariants = cva(
  'relative inline-flex max-w-full flex-nowrap overflow-x-auto no-scrollbar rounded-full p-1',
  {
    variants: {
      variant: {
        default: 'bg-surface-foreground',
        fullWidth: 'w-fit justify-start align-baseline',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

type TabsListProps = React.ComponentPropsWithoutRef<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants> & {
    initSelectedIndex?: number;
  };

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  TabsListProps
>(({ className, variant, children, initSelectedIndex, ...props }, ref) => {
  const [selectedIndex, setSelectedIndex] = React.useState(
    initSelectedIndex ?? 0,
  );
  const [pillWidth, setPillWidth] = React.useState(0);
  const [pillTranslateX, setPillTranslateX] = React.useState(0);
  const buttonRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

  // Update pill position and width when selectedIndex changes.
  React.useEffect(() => {
    const currentButton = buttonRefs.current[selectedIndex];
    if (currentButton) {
      const { offsetLeft, offsetWidth } = currentButton;
      setPillTranslateX(offsetLeft);
      setPillWidth(offsetWidth);
    }
  }, [selectedIndex, children]);

  React.useEffect(() => {
    if (initSelectedIndex !== undefined) {
      setSelectedIndex(initSelectedIndex);
    }
  }, [initSelectedIndex]);

  // Clone children to attach refs and track selection for the pill animation.
  const clonedChildren = React.Children.map(children, (child, index) => {
    if (React.isValidElement(child)) {
      const childProps = {
        ref: (el: HTMLButtonElement) => (buttonRefs.current[index] = el),
        onClick: () => setSelectedIndex(index),
      };
      return React.cloneElement(
        child as React.ReactElement<Record<string, unknown>>,
        childProps,
      );
    }
    return child;
  });

  return (
    <TabsPrimitive.List
      className={cn(
        'bg-surface-foreground',
        tabsListVariants({ variant, className }),
      )}
      ref={ref}
      {...props}
    >
      {/* Animated active pill — anchored physically so it composes with the
          translateX driven by offsetLeft (both LTR coordinate spaces). */}
      <div
        className="absolute top-[3px] left-0 h-10 rounded-full border border-border-medium bg-surface-midground transition-all duration-300"
        style={{
          width: pillWidth,
          transform: `translateX(${pillTranslateX}px)`,
        }}
      />
      {clonedChildren}
    </TabsPrimitive.List>
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const label = typeof children === 'string' ? children : undefined;
  return (
    <TabsPrimitive.Trigger
      className={cn(
        'relative z-20 whitespace-nowrap rounded-full px-4 py-2 text-content-primary text-sm transition-colors duration-300 hover:bg-surface-midground/50 focus:outline-none data-[state=active]:font-semibold',
        // Reserve width for the bold label so the pill doesn't reflow on select.
        'after:invisible after:block after:h-0 after:font-semibold after:content-[attr(data-label)]',
        className,
      )}
      data-label={label}
      ref={ref}
      {...props}
    >
      {children}
    </TabsPrimitive.Trigger>
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    className={cn(
      'mt-4 ring-offset-surface-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-action focus-visible:ring-offset-2',
      className,
    )}
    ref={ref}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
